import {createControlledBotsMaskedStore} from "./online/controlledBots";
import {createAutopilotMaskedStore} from "./online/autopilotPlayers";
import {maybeExecuteTutorialBotStep, TUTORIAL_BOT_WAITING} from '../tutorial/tutorialBotScript';
import {checkIfTutorialActive} from '../tutorial/checkIfTutorialActive';
import {maybeExecuteMissionBotStep, MISSION_BOT_WAITING} from '../missions/missionBotScript';
import {
  selectCurrentPlayer,
  selectGameResults,
  selectGameType,
  selectIsGameStarted,
  selectPendingMeleeDamage,
  selectPhase,
  selectPlayers,
  selectPlayersConfirmedCombatStage,
  selectPlayersWaitedToConfirmCombatStage,
  selectRound,
  selectSharedGameState,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {checkIfParallelSupplyPhase} from "../gameState/supply/parallelSupply";
import {ensureGameStrategyReset} from "./strategy/botStrategyStore";
import {evaluateArchetype} from "./strategy/evaluateArchetype";
import {ensureBonusAxis} from "./strategy/bonusAxis";
import {getBotBonusProfile} from "./getBotBonusProfile";
import {GAME_TYPES} from "../../constants/game";
import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import {executeWarPhaseStageCompletion, handleCompleteTurn} from "../gameState/turn/handleCompleteTurn";
import {confirmCombatStage, setSelectedToken, showToast} from "../../redux/actions";
import {checkIfAllPlayersConfirmedCombatStage} from "../gameState/melee/checkIfAllPlayersConfirmedCombatStage";
import {getHumanMeleeCompletingPlayer} from "../gameState/melee/getHumanMeleeCompletingPlayer";
import {createBotEffectsStore, getBotSoundManager} from "./botEffects";
import {decideSetupStep} from "./decisions/decideSetupStep";
import {decideSupplyStep} from "./decisions/decideSupplyStep";
import {decideConstructionStep} from "./decisions/decideConstructionStep";
import {sanitizeInvalidPendingBuildings} from "./decisions/sanitizeInvalidPendingBuildings";
import {decideRecruitmentStep} from "./decisions/decideRecruitmentStep";
import {decideMovementStep} from "./decisions/decideMovementStep";
import {decideShootingStep} from "./decisions/decideShootingStep";
import {decideCombatStep} from "./decisions/decideCombatStep";
import {decideManeuverStep} from "./decisions/decideManeuverStep";
import {decideAbilitiesStep} from "./decisions/decideAbilitiesStep";
import {BOT_MAX_STEPS_PER_PHASE} from "./botConfig";
import {ensureOpponentPredictions} from "./strongMode/simulateOpponentsWarPhases";
import {clearBotPredictions} from "./strongMode/botPredictionsStore";
import {clearPlannerPlans} from "./planner/plannerStore";
import {BOT_PHASE_COMPLETION_MODES, BOT_STEP_RESULTS} from "./botStepResults";
import {checkIfBotWantsToBuyPolicy, checkIfBotWantsToRefreshPolicyMarket} from "./decisions/decidePolicyStep";

// Счетчик шагов в рамках одной фазы (страховка от зацикливания)
let stepCounterKey = null;
let stepCounter = 0;

/**
 * Сброс модульных счетчиков движка ботов. Нужен при запуске новой локальной
 * партии с теми же id игроков (демо в меню: id выводятся из цветов поля) —
 * иначе ключ шага новой партии может совпасть с ключом прошлой, и кап шагов
 * досрочно завершит фазу (например, расстановку стартовых кварталов).
 */
export const resetBotStepGuards = () => {
  stepCounterKey = null;
  stepCounter = 0;
  meleeWaitToastKey = null;
};

const getStepCounterKey = (state) => {
  const round = selectRound(state);
  const phase = selectPhase(state);
  const warPhaseStage = selectWarPhaseStage(state);
  const currentPlayer = selectCurrentPlayer(state);
  return `${round}|${phase}|${warPhaseStage || ''}|${currentPlayer?.id || ''}`;
};

const completeBotPhase = (store, options = {}) => {
  const state = store.getState();
  const phase = selectPhase(state);

  if (phase === PHASES.CONSTRUCTION) {
    // Невалидные pending-строения заблокировали бы завершение фазы — откатываем их
    sanitizeInvalidPendingBuildings(store);
    store.dispatch(setSelectedToken(null));
  }

  // В браузере завершение фаз бота озвучивается так же, как нажатие кнопки живым игроком;
  // в headless-симуляциях и песочнице сильного режима звуки не проигрываются.
  // serverExecutor: сервер исполняет ход сам — timestamp он выставляет в исходных
  // экшенах (как при таймауте хода), а не отдельным серверным экшеном
  const soundManager = store.__isBotSandbox ? null : getBotSoundManager();
  handleCompleteTurn(store, !options.serverExecutor, Boolean(soundManager), soundManager);
};

// Store-обертка сетевого исполнения бота: тосты бота не должны показываться
// на экране делегата (живого игрока, которому сервер делегировал ход бота)
const createNoToastStore = (store) => {
  const noToastStore = {
    getState: () => store.getState(),
    dispatch: (action) => {
      if (action?.type === 'SHOW_TOAST') {
        return undefined;
      }
      return store.dispatch(action);
    },
  };
  // Маркер песочницы сильного режима обязан пережить обертку. Иначе внутри
  // онлайн-executeBotStep песочницы (options.online === true) обертка «съедает»
  // флаг, и createBotEffectsStore / decideMovementStep перестают распознавать
  // песочницу — симулируемые чужие ходы проигрывают реальные эффекты и
  // анимируют реальные отряды на экране делегата (чужие отряды «двигаются» и
  // откатываются в начале хода бота). Прокидываем флаг на обертку.
  if (store.__isBotSandbox) {
    noToastStore.__isBotSandbox = true;
  }
  return noToastStore;
};

/**
 * Онлайн-этап ближнего боя: подтверждения за всех неподтвердивших ботов-участников
 * (зеркало онлайн-ветки CompletePhaseButton). Если бот подтверждает последним —
 * этап завершает он же. Возвращает true, если было отправлено хотя бы одно подтверждение.
 */
const confirmPendingBotCombatStage = (store, options = {}) => {
  const state = store.getState();
  const waitedIds = selectPlayersWaitedToConfirmCombatStage(state);
  const confirmedIds = new Set(selectPlayersConfirmedCombatStage(state));
  const pendingBots = selectPlayers(state).filter(
    (player) => player.isBot && waitedIds.includes(player.id) && !confirmedIds.has(player.id)
  );

  if (pendingBots.length === 0) {
    return false;
  }

  pendingBots.forEach((bot) => store.dispatch(confirmCombatStage(bot.id)));

  if (checkIfAllPlayersConfirmedCombatStage(store.getState())) {
    executeWarPhaseStageCompletion(store, !options.serverExecutor, false);
  }
  return true;
};

// Ключ этапа ближнего боя, для которого уже показана подсказка живому игроку
let meleeWaitToastKey = null;

/**
 * Возвращает живого игрока, который должен завершить этап ближнего боя бота,
 * или null, если в состоявшихся ближних боях участвуют только боты/нейтралы.
 */
const getMeleeWaitCompletingPlayer = (store) => {
  const state = store.getState();
  if (selectPhase(state) !== PHASES.WAR) return null;
  if (selectWarPhaseStage(state) !== WAR_PHASE_STAGES.COMBAT) return null;

  return getHumanMeleeCompletingPlayer(selectPlayers(state), selectPendingMeleeDamage(state));
};

/**
 * Есть ли среди участников состоявшихся ближних боев отряды ботов.
 * По правилам перебросы/приоритеты доступны владельцам ВСЕХ участвующих отрядов,
 * а не только текущему игроку — боты-защитники тоже действуют.
 */
export const checkIfBotMeleeParticipantsPresent = (state) => {
  const pendingMeleeDamage = selectPendingMeleeDamage(state);
  if (pendingMeleeDamage.length === 0) {
    return false;
  }
  const participantOwnerIds = new Set(pendingMeleeDamage.map((action) => action.ownerId));
  return selectPlayers(state).some((player) => player.isBot && participantOwnerIds.has(player.id));
};

/**
 * Выполняет один видимый шаг бота: одно игровое действие или завершение фазы/этапа.
 * Вызывается BotRunner-ом с интервалом BOT_ACTION_INTERVAL_MS.
 *
 * options.strongMode — «сильный режим»: перед этапами своей фазы войны бот
 * симулирует в песочнице фазы войны следующих противников раунда (основным
 * алгоритмом) и учитывает прогноз в своих решениях.
 *
 * options.phaseCompletion — завершение фазы строительства
 * (BOT_PHASE_COMPLETION_MODES), по умолчанию AUTO.
 *
 * options.holdPolicyPurchase — в фазе строительства не покупать политику: если
 * бот хочет ее купить, шаг ничего не делает и возвращает
 * POLICY_PURCHASE_WANTED. Покупку сделает следующий обычный шаг. Желание
 * обновить рынок политик (волна 36, боты золото+) придерживается так же: план
 * живого игрока рынок не обновляет, он жмет кнопку сам.
 *
 * Возвращает итог шага (BOT_STEP_RESULTS) или undefined, если бот не ходил.
 */
export const executeBotStep = (store, options = {}) => {
  // Игроки на автопилоте (ИИ промо-матча, за которого временно ходит
  // серверный бот, см. autopilotPlayers) для движка — боты: он ходит за них и
  // подтверждает за них этап ближнего боя. Маска стоит ДО маски перехвата:
  // так последнее слово за живым человеком (см. autopilotPlayers)
  if (options.autopilotPlayerIds && options.autopilotPlayerIds.length > 0) {
    store = createAutopilotMaskedStore(store, options.autopilotPlayerIds);
  }

  // Боты, которыми сейчас вручную управляет онлайн-админ (перехват управления),
  // для движка — живые игроки: он не ходит за них и не подтверждает за них этап
  // ближнего боя. Список присылает сервер (см. controlledBots)
  if (options.controlledBotIds && options.controlledBotIds.length > 0) {
    store = createControlledBotsMaskedStore(store, options.controlledBotIds);
  }

  // Сетевой режим (делегированный клиенту ход бота или серверный fallback):
  // тосты бот-движка не должны попадать на экран делегата
  if (options.online) {
    store = createNoToastStore(store);
  }

  const state = store.getState();

  // Повторная проверка условий на свежем состоянии (авто-действия могли продвинуть игру)
  const expectedGameType = options.online ? GAME_TYPES.ONLINE : GAME_TYPES.LOCAL;
  if (selectGameType(state) !== expectedGameType) return;
  if (!selectIsGameStarted(state)) return;
  if (selectGameResults(state)) return;

  // Одновременная фаза снабжения сетевой игры: текущий игрок в ней только якорь
  // слепка хода, а снабжение ботов раздает и завершает серверный планировщик
  // (executeParallelSupplyBotTurn) — работу бота делегату сервер не выдает, и
  // ходить здесь не за кого
  if (checkIfParallelSupplyPhase(selectSharedGameState(state))) {
    return;
  }

  const currentPlayer = selectCurrentPlayer(state);
  if (!currentPlayer || !currentPlayer.isBot) {
    // Режим защитника: во время этапа ближнего боя ЧУЖОГО (живого) игрока боты,
    // чьи отряды участвуют в боях, делают свои перебросы и приоритетные цели.
    // В локальной игре этап завершает текущий игрок; в сетевой боты-участники
    // дополнительно дают свои подтверждения этапа, когда действий больше нет
    if (selectPhase(state) === PHASES.WAR
      && selectWarPhaseStage(state) === WAR_PHASE_STAGES.COMBAT
      && checkIfBotMeleeParticipantsPresent(state)
    ) {
      try {
        const acted = decideCombatStep(createBotEffectsStore(store));
        if (!acted && options.online) {
          confirmPendingBotCombatStage(store, options);
        }
      } catch (error) {
        console.error('[Bot] Defender combat step failed', error);
      }
    }
    return;
  }

  // Стратегическое состояние: сброс при новой партии + переоценка архетипа раз в раунд
  if (ensureGameStrategyReset(selectPlayers(state))) {
    clearBotPredictions();
    clearPlannerPlans();
  }
  evaluateArchetype(state, currentPlayer);
  // Волна 33: ось удара бонусного бота (ближайший противник, кузница, тыл) —
  // раз в раунд, до любого решения фазы; для ботов без пакета — null
  ensureBonusAxis(state, currentPlayer, getBotBonusProfile(currentPlayer, selectGameType(state)));

  const currentKey = getStepCounterKey(state);
  if (stepCounterKey !== currentKey) {
    stepCounterKey = currentKey;
    stepCounter = 0;
  }
  stepCounter += 1;

  if (stepCounter > BOT_MAX_STEPS_PER_PHASE) {
    console.error(`[Bot] Step cap reached (${BOT_MAX_STEPS_PER_PHASE}) for ${currentKey}, forcing phase completion`);
    completeBotPhase(store, options);
    return BOT_STEP_RESULTS.COMPLETED;
  }

  const phase = selectPhase(state);
  const phaseCompletion = phase === PHASES.CONSTRUCTION
    ? (options.phaseCompletion || BOT_PHASE_COMPLETION_MODES.AUTO)
    : BOT_PHASE_COMPLETION_MODES.AUTO;

  if (phaseCompletion === BOT_PHASE_COMPLETION_MODES.FORCE) {
    completeBotPhase(store, options);
    return BOT_STEP_RESULTS.COMPLETED;
  }

  // Сильный режим: прогноз фаз войны следующих противников раунда (кэш на раунд).
  // В песочнице прогнозы не считаются — вложенных симуляций нет
  if (options.strongMode && phase === PHASES.WAR && !store.__isBotSandbox) {
    try {
      ensureOpponentPredictions(store, currentPlayer, options);
    } catch (error) {
      console.error('[Bot] Strong mode predictions failed', error);
    }
  }

  // Обертка store с озвучкой действий бота (в headless-симуляциях возвращает store как есть)
  const effectsStore = createBotEffectsStore(store);

  try {
    let acted = false;

    // Режим обучения: бот исполняет детерминированный сценарий главы вместо
    // decision-движка (null = обучение неактивно, работаем как обычно)
    // Сюжетная миссия: вместо решения бота воспроизводим записанный редактором
    // ход противника (запись кончилась — падаем в обычный decision-движок)
    const missionScriptResult = maybeExecuteMissionBotStep(effectsStore);
    if (missionScriptResult === MISSION_BOT_WAITING) {
      stepCounter = 0;
      return;
    }
    if (missionScriptResult === true) {
      return;
    }

    const tutorialScriptResult = maybeExecuteTutorialBotStep(effectsStore);
    if (tutorialScriptResult === TUTORIAL_BOT_WAITING) {
      // Бот ждет игрока (например, закрытия обучающей модалки): тик не
      // считается шагом, иначе кап шагов досрочно завершил бы этап
      stepCounter = 0;
      return;
    }
    if (tutorialScriptResult !== null) {
      acted = tutorialScriptResult;
    } else if (phase === PHASES.SETUP) {
      acted = decideSetupStep(effectsStore);
    } else if (phase === PHASES.SUPPLY) {
      acted = decideSupplyStep(effectsStore);
    } else if (phase === PHASES.CONSTRUCTION) {
      if (options.holdPolicyPurchase && (
        checkIfBotWantsToBuyPolicy(effectsStore.getState())
        || checkIfBotWantsToRefreshPolicyMarket(effectsStore.getState(), {
          isSandbox: Boolean(effectsStore.__isBotSandbox),
        })
      )) {
        return BOT_STEP_RESULTS.POLICY_PURCHASE_WANTED;
      }
      acted = decideConstructionStep(effectsStore);
    } else if (phase === PHASES.WAR) {
      const warPhaseStage = selectWarPhaseStage(state);
      if (warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT) {
        acted = decideRecruitmentStep(effectsStore);
      } else if (warPhaseStage === WAR_PHASE_STAGES.MOVEMENT) {
        acted = decideMovementStep(effectsStore);
      } else if (warPhaseStage === WAR_PHASE_STAGES.ABILITIES) {
        // Волна 39: соблазнение суккубов и способности гоблинов (до волны
        // боты этап пропускали — эти фракции им были запрещены)
        acted = decideAbilitiesStep(effectsStore);
      } else if (warPhaseStage === WAR_PHASE_STAGES.SHOOTING) {
        acted = decideShootingStep(effectsStore);
      } else if (warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
        acted = decideCombatStep(effectsStore);
      } else if (warPhaseStage === WAR_PHASE_STAGES.MANEUVER) {
        acted = decideManeuverStep(effectsStore);
      }
    }

    if (!acted) {
      // Обычный матч, этап ближнего боя своего хода: завершение через механику
      // подтверждений всех участников — бот подтверждает за себя, а если люди
      // еще не подтвердили, ждет их (последний подтвердивший завершает этап)
      if (options.online && phase === PHASES.WAR
        && selectWarPhaseStage(store.getState()) === WAR_PHASE_STAGES.COMBAT
      ) {
        const didConfirm = confirmPendingBotCombatStage(store, options);
        if (!didConfirm && !checkIfAllPlayersConfirmedCombatStage(store.getState())) {
          stepCounter = 0; // кап шагов не должен принудительно завершить этап во время ожидания
        }
        return;
      }

      // Этап ближнего боя с отрядами живых игроков завершает назначенный живой игрок
      // по кнопке завершения этапа — бот ждет и не завершает этап сам
      const meleeCompletingPlayer = getMeleeWaitCompletingPlayer(store);
      if (!options.online && meleeCompletingPlayer) {
        stepCounter = 0; // кап шагов не должен принудительно завершить этап во время ожидания

        // В обучении этот тост не нужен: что делать в бою, игроку говорят
        // задание шага и обучающие модалки, а общий текст поверх них только
        // сбивает (ходы бота в главе ведет сценарий — см. tutorialBotScript)
        if (meleeWaitToastKey !== currentKey && !checkIfTutorialActive(store.getState())) {
          meleeWaitToastKey = currentKey;
          store.dispatch(showToast(
            `${meleeCompletingPlayer.name}, в ближнем бою участвуют Ваши отряды!\n\nПеребросьте кости или выберите приоритетные цели.\nЗатем завершите этап кнопкой завершения хода.`,
            'info', 7000
          ));
        }
        return;
      }

      if (phaseCompletion === BOT_PHASE_COMPLETION_MODES.DEFER) {
        return BOT_STEP_RESULTS.READY_TO_COMPLETE;
      }
      completeBotPhase(store, options);
      return BOT_STEP_RESULTS.COMPLETED;
    }
    return BOT_STEP_RESULTS.ACTED;
  } catch (error) {
    // Бот не должен блокировать игру: при ошибке решения завершаем фазу
    console.error('[Bot] Decision step failed, completing phase', error);
    try {
      completeBotPhase(store, options);
    } catch (completionError) {
      console.error('[Bot] Phase completion also failed', completionError);
    }
    return BOT_STEP_RESULTS.COMPLETED;
  }
};
